/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2018, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./QueryResultData',
	'./Query',
	'./api/QueryServiceAPI'
], (APIFactory, QueryResultData, Query, QueryServiceAPI) => {

	class QueryService {

		constructor(options) {
			const internalQueryService = options.features['QueryService.internal'];

			this.internalQueryService = internalQueryService;
			this.dashboard = options.features['API'];
		}

		getAPI() {
			if (!this.queryServiceAPI) {
				this.queryServiceAPI = APIFactory.createAPI(this, [QueryServiceAPI]);
			}
			return this.queryServiceAPI;
		}

		executeQuery(sourceId, querySpec, senderId, requestOptions) {
			return this.internalQueryService.executeQuery(sourceId, querySpec, senderId, requestOptions).then(queryResultData => {
				const queryResult = new QueryResultData(queryResultData.data);
				queryResult.setPropertyValue('QuerySpec.internal', JSON.stringify(querySpec));
				// Today we don't have access to the raw resposne becasue we use MUI to get the data
				// TODO We should call the ajax service ourself and stop relying on MUI.
				queryResult.setPropertyValue('RawData.internal', JSON.stringify(queryResultData.data));
				queryResult.setPropertyValue('RequestTime.internal', queryResultData.requestTime || null );
				return queryResult.getAPI();
			});
		}

		createQuery(type) {
			const query = new Query(this.dashboard, this, type);
			return query.getAPI();
		}

		getColumnValues(sourceId, itemIdList = [], filterList = [], sort, searchTermList = [], aggregationList = null) {
			if(!Array.isArray(itemIdList)) {
				itemIdList = [itemIdList];
			}

			const query = this.createQuery();
			return query.setDataSource(sourceId).then(() => {
				const dataItems = query.addColumns(itemIdList);
				dataItems.forEach((dataItem, i) => {
					if (!dataItem.getSort() && sort) {
						dataItem.setSort({ type: sort });
					}

					//since we're expecting to see discrete set of values in the filter flyout, set the aggregation to null here
					dataItem.setAggregation(aggregationList && aggregationList[i] || null);

					if (searchTermList.length > 0) {
						query.addSearchTerms(dataItem.getColumnId(), searchTermList);
					}
				});
				query.addFilters(filterList);

				return query.executeQuery();
			});
		}

		getColumnMinMaxValues (sourceId, itemId, filters = []) {
			const query = this.createQuery();
			return query.setDataSource(sourceId).then(() => {
				const dataItems = query.addColumns([itemId, itemId]);
				dataItems[0].setAggregation('min');
				dataItems[1].setAggregation('max');
				if(filters.length) {
					query.addFilters(filters);
				}
				return query.executeQuery();
			});
		}

		getDataItemsMinMaxValues(sourceId, dataItems, projections, filterList) {
			const query = this.createQuery('minmax');
			return query.setDataSource(sourceId).then(() => {
				dataItems.forEach(dataItem => {
					const columnId = dataItem.getColumnId();
					if (columnId) {
						let queryDataItems = query.addColumns([columnId]);
						let queryDataItem = queryDataItems[queryDataItems.length - 1];
						queryDataItem.setType(dataItem.getType());
						let isProjected = projections.indexOf(dataItem.getId()) !== -1;
						if (isProjected) {
							queryDataItem.setAggregation(dataItem.getAggregation());
						}
						queryDataItem.setProjected(isProjected);
					}
				});

				if (filterList && filterList.length) {
					query.addFilters(filterList);
				}
				return query.executeQuery();
			});
		}

		getRootMembers(sourceId, itemIds=[], filters=[], rowLimit = 51) {
			if (!Array.isArray(itemIds)) {
				itemIds = [itemIds];
			}

			const query = this.createQuery();
			return query.setDataSource(sourceId).then(() => {
				const dataItems = query.addColumns(itemIds);
				dataItems.forEach((dataItem) => {
					dataItem.addCommand({
						operation: 'add',
						rootMembers: 'true'
					});
				});

				query.addFilters(filters);
				query.setLimit(rowLimit);

				return query.executeQuery();
			});
		}

		getChildren(sourceId, itemIds=[], memberId, filters=[], rowLimit = 51, offset = 0) {
			if (!Array.isArray(itemIds)) {
				itemIds = [itemIds];
			}

			const query = this.createQuery();
			return query.setDataSource(sourceId).then(() => {
				const dataItems = query.addColumns(itemIds);
				dataItems.forEach((dataItem) => {
					dataItem.addCommand({
						operation: 'add',
						children: memberId
					});
				});

				query.addFilters(filters);
				query.setLimit(rowLimit);
				query.setOffset(offset);

				return query.executeQuery();
			});
		}

		getAscendants(sourceId, itemIds=[], memberIds=[], filters=[]) {
			if (!Array.isArray(itemIds)) {
				itemIds = [itemIds];
			}

			const query = this.createQuery();
			return query.setDataSource(sourceId).then(() => {
				const dataItems = query.addColumns(itemIds);
				dataItems.forEach((dataItem) => {
					dataItem.addCommand({
						operation: 'add',
						ascendants: memberIds
					});
				});

				query.addFilters(filters);

				return query.executeQuery();
			});
		}

		// See FilterQueryHelper.getSiblings for notes
		getSiblings(sourceId, itemIds=[], memberId, filters=[], rowLimit = 51) {
			if (!Array.isArray(itemIds)) {
				itemIds = [itemIds];
			}

			const query = this.createQuery();
			return query.setDataSource(sourceId).then(() => {
				const dataItems = query.addColumns(itemIds);
				dataItems.forEach((dataItem) => {
					dataItem.addCommand({
						operation: 'add',
						siblings: memberId
					});
				});

				query.addFilters(filters);
				query.setLimit(rowLimit);

				return query.executeQuery();
			});
		}


	}

	return QueryService;
});
